/**
 * ImportDropzone — AK-368c дропзона «📁 Загрузить файл» (третий способ в режиме
 * «+ Поле», фрейм 1).
 *
 * Рендерится внутри AddFieldPanel (armed-фаза «+ Поле»). Drag&drop / файлпикер
 * (KML, KMZ, SHP-zip, GeoJSON, до 500 контуров) → POST preview → importStore.
 * Клиентская проверка расширения + мгновенный фидбэк; парсинг/ошибки ведёт
 * importStore (ImportFilePanel показывает лоадер/ошибку/превью).
 *
 * DS: только @/components/ui/* + Lucide + Earth Tech токены (accent-brand для
 * пунктирной рамки/акцента). Ноль hex.
 */
import { useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import { useImportStore } from '@/lib/map/import/importStore';
import { previewImport, ACCEPTED_EXTENSIONS, ImportFileError } from '@/lib/map/import/importApi';
import { useAvailableScopes } from '@/components/scope/useAvailableScopes';
import { sessionFarmIdOf } from '@/components/scope/sessionFarm';

/** Расширение файла в нижнем регистре, включая точку («zarya.kmz» → «.kmz»). */
function extOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i).toLowerCase() : '';
}

export function ImportDropzone() {
  const beginParse = useImportStore((s) => s.beginParse);
  const setPreview = useImportStore((s) => s.setPreview);
  const failFile = useImportStore((s) => s.failFile);
  // ADR-081 rev.6 D5: превью для админа хозяйства — по хозяйству из переключателя.
  const sessionFarmId = sessionFarmIdOf(useAvailableScopes().data);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File): Promise<void> => {
    if (!ACCEPTED_EXTENSIONS.includes(extOf(file.name))) {
      failFile(
        'unreadable',
        'Неподдерживаемый формат. Загрузите KML, KMZ, SHP (zip) или GeoJSON.',
      );
      return;
    }
    beginParse(file.name);
    try {
      const res = await previewImport(file, sessionFarmId);
      setPreview(res.features, res.warnings);
    } catch (err) {
      if (err instanceof ImportFileError) {
        failFile(err.kind, err.message);
      } else {
        failFile('network', 'Не удалось загрузить файл. Попробуйте ещё раз.');
      }
    }
  };

  return (
    <div
      data-testid="import-dropzone"
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) void handleFile(file);
      }}
      onClick={() => inputRef.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          inputRef.current?.click();
        }
      }}
      className={
        // Непрозрачная поверхность (bg-popover), НЕ полупрозрачность над картой —
        // иначе спутник просвечивает и текст нечитаем (AK-369 founder UAT).
        'cursor-pointer rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors ' +
        (dragOver
          ? 'border-accent-brand bg-accent-brand/15'
          : 'border-accent-brand/60 bg-popover hover:bg-accent')
      }
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_EXTENSIONS.join(',')}
        className="hidden"
        data-testid="import-file-input"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleFile(file);
          // Сброс, чтобы повторный выбор того же файла тоже стриггерил change.
          e.target.value = '';
        }}
      />
      <Upload size={24} className="mx-auto text-accent-brand mb-2" />
      <div className="text-sm font-bold text-foreground">Перетащите файл сюда</div>
      <div className="text-xs text-muted-foreground mt-1">
        или <span className="text-accent-brand font-semibold">выберите на диске</span>
      </div>
      <div className="text-xs text-muted-foreground mt-2 font-semibold">
        KML · KMZ · SHP (zip) · GeoJSON — до 500 полей
      </div>
    </div>
  );
}
